// components/industry-templates/InteriorDesignEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Home, Palette, Sofa, Award, Sparkles, Ruler } from 'lucide-react';

export const InteriorDesignEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Event Description */}
      <Card className="border-0 bg-gradient-to-br from-pink-500/20 to-rose-500/20 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-2xl">
            <Home className="w-8 h-8" style={{ color: theme.primary }} />
            Interior Design Expo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-300 text-lg leading-relaxed mb-6">{structure.description}</p>
          
          {/* Show home_decor indicator if available */}
          {industryData?.home_decor && (
            <div className="p-5 bg-white/10 rounded-xl text-center">
              <div className="text-4xl mb-3">🏠</div>
              <p className="text-2xl font-bold text-white mb-2">Home Décor Showcase</p>
              <p className="text-slate-300">Explore latest trends in home decoration and styling</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Topics Covered */}
      {structure.topics && structure.topics.length > 0 && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-6 h-6" style={{ color: theme.primary }} />
              Design Topics & Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {structure.topics.map((topic: string, idx: number) => (
                <Badge key={idx} style={{ backgroundColor: theme.primary }} className="text-lg py-3 px-6">
                  {topic}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Interior Design Showcase */}
      <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(236, 72, 153, 0.1)' }}>
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Palette className="w-6 h-6" style={{ color: theme.primary }} />
            Design Exhibition Highlights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { icon: '🛋️', title: 'Modern Living Spaces', desc: 'Contemporary furniture and minimalist designs' },
              { icon: '🎨', title: 'Color Palettes', desc: 'Latest color trends for 2026' },
              { icon: '💡', title: 'Smart Lighting', desc: 'Innovative lighting solutions and automation' },
              { icon: '🪟', title: 'Sustainable Materials', desc: 'Eco-friendly and sustainable design options' },
              { icon: '🏡', title: 'Home Offices', desc: 'Modern workspace design and ergonomics' },
              { icon: '🌿', title: 'Biophilic Design', desc: 'Bringing nature indoors with plants and natural elements' }
            ].map((item, idx) => (
              <div key={idx} className="p-5 bg-white/10 rounded-xl border-l-4 hover:bg-white/15 transition-colors" style={{ borderColor: theme.primary }}>
                <div className="flex items-start gap-3">
                  <div className="text-3xl">{item.icon}</div>
                  <div>
                    <h4 className="font-bold text-white mb-1 text-lg">{item.title}</h4>
                    <p className="text-sm text-slate-400">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Design Styles */}
      <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Sofa className="w-6 h-6" style={{ color: theme.primary }} />
            Featured Design Styles
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              'Modern Minimalist',
              'Scandinavian',
              'Industrial Chic',
              'Mid-Century Modern',
              'Bohemian',
              'Contemporary',
              'Rustic',
              'Art Deco',
              'Japanese Zen'
            ].map((style, idx) => (
              <div key={idx} className="p-4 bg-white/5 rounded-lg text-center border border-white/10 hover:scale-105 transition-transform">
                <p className="text-white font-medium">{style}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Design Workshops */}
      <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Ruler className="w-6 h-6" style={{ color: theme.primary }} />
            Interior Design Workshops
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              'Space Planning & Layout Design',
              'Color Theory for Interiors',
              '3D Visualization & Rendering',
              'Furniture Selection & Placement',
              'Lighting Design Fundamentals',
              'Budget-Friendly Design Tips'
            ].map((workshop, idx) => (
              <div key={idx} className="p-4 bg-white/5 rounded-lg flex items-center gap-3 hover:bg-white/10 transition-colors">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${theme.primary}30` }}>
                  <span className="text-xl" style={{ color: theme.primary }}>✓</span>
                </div>
                <p className="text-white font-medium">{workshop}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Event Tags */}
      {structure.tags && structure.tags.length > 0 && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-6 h-6" style={{ color: theme.primary }} />
              Event Tags
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {structure.tags.map((tag: string, idx: number) => (
                <Badge key={idx} variant="outline" className="border-white/20 text-white text-sm py-2 px-4">
                  #{tag}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Organizer Information */}
      <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white">Organized By</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center text-3xl" style={{ backgroundColor: theme.primary }}>
              🏠
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-white text-xl mb-2">{structure.organizer_name}</h4>
              <div className="space-y-2 text-slate-300">
                {structure.organizer_email && (
                  <p className="flex items-center gap-2">
                    <span>📧</span> {structure.organizer_email}
                  </p>
                )}
                {structure.organizer_phone && (
                  <p className="flex items-center gap-2">
                    <span>📞</span> {structure.organizer_phone}
                  </p>
                )}
                {structure.organizer_website && (
                  <a href={structure.organizer_website} target="_blank" rel="noopener noreferrer" 
                     className="flex items-center gap-2 text-blue-400 hover:underline">
                    <span>🌐</span> {structure.organizer_website}
                  </a>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
